/* From the Mist — theme JS. Vanilla ES, no deps.
   Hook contract (used by Liquid):
   - Triggers:  [data-open="cart|search|mobile"]
   - Panels:    [data-drawer="cart|search|mobile"]  (with [data-close] buttons inside)
   - Scrim:     [data-scrim]
   - Cart count:[data-cart-count]
   - PDP add:   [data-add="{variantId}"]  (+ optional [data-qty-input])
   - Cart line: [data-line-key], [data-qty="inc|dec"], [data-remove]
   All motion respects prefers-reduced-motion via CSS.
*/
(function () {
  'use strict';

  /* ---------- Drawers ---------- */
  const scrim = () => document.querySelector('[data-scrim]');
  let lastFocus = null;

  function panelFor(name) {
    return document.querySelector(`[data-drawer="${name}"]`);
  }

  function openDrawer(name) {
    const panel = panelFor(name);
    if (!panel) return;
    lastFocus = document.activeElement;
    document
      .querySelectorAll('[data-drawer]')
      .forEach((p) => closePanel(p, true));
    panel.dataset.open = 'true';
    panel.setAttribute('aria-hidden', 'false');
    const s = scrim();
    if (s) {
      s.classList.remove('invisible', 'opacity-0');
      s.classList.add('opacity-100');
    }
    document.body.style.overflow = 'hidden';
    const focusable = panel.querySelector(
      'a,button,input,select,textarea,[tabindex]:not([tabindex="-1"])',
    );
    if (focusable) focusable.focus();
  }

  function closePanel(panel, silent) {
    if (!panel || panel.dataset.open !== 'true') return;
    panel.dataset.open = 'false';
    panel.setAttribute('aria-hidden', 'true');
    if (!silent) {
      const s = scrim();
      if (s) {
        s.classList.add('opacity-0');
        s.classList.remove('opacity-100');
        setTimeout(() => s.classList.add('invisible'), 300);
      }
      document.body.style.overflow = '';
      if (lastFocus) lastFocus.focus();
    }
  }

  function closeAll() {
    document.querySelectorAll('[data-drawer]').forEach((p) => closePanel(p));
    const s = scrim();
    if (s) {
      s.classList.add('opacity-0');
      s.classList.remove('opacity-100');
      setTimeout(() => s.classList.add('invisible'), 300);
    }
    document.body.style.overflow = '';
  }

  const DRAWER_NAMES = ['cart', 'search', 'mobile'];
  document.addEventListener('click', (e) => {
    // Only treat real triggers (data-open="cart|search|mobile") as openers.
    // The drawer panels also carry data-open="true|false" for their slide
    // state, so guard by value or clicks inside an open drawer get hijacked.
    const opener = e.target.closest('[data-open]');
    if (opener && DRAWER_NAMES.includes(opener.getAttribute('data-open'))) {
      e.preventDefault();
      openDrawer(opener.getAttribute('data-open'));
      return;
    }
    if (e.target.closest('[data-close]') || e.target.closest('[data-scrim]')) {
      closeAll();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAll();
  });

  // Focus trap within an open drawer
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const panel = document.querySelector('[data-drawer][data-open="true"]');
    if (!panel) return;
    const f = panel.querySelectorAll(
      'a,button,input,select,textarea,[tabindex]:not([tabindex="-1"])',
    );
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ---------- Cart (AJAX) ---------- */
  async function postJSON(url, body) {
    const r = await fetch(url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(body),
    });
    return r.json();
  }

  async function getCart() {
    return fetch('/cart.js').then((r) => r.json());
  }

  function updateCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.classList.toggle('hidden', cart.item_count === 0);
    });
  }

  async function rerenderCart() {
    const cart = await getCart();
    updateCount(cart);
    // Re-render cart drawer + page via Section Rendering API
    const ids = ['cart-drawer', 'main-cart'];
    await Promise.all(
      ids.map(async (id) => {
        const host = document.querySelector(`[data-section="${id}"]`);
        if (!host) return;
        // Preserve drawer open/closed state — the freshly rendered section
        // defaults to data-open="false", which would otherwise slide the
        // drawer shut on every qty change / remove.
        const wasOpen = host.getAttribute('data-open');
        const html = await fetch(`${location.pathname}?section_id=${id}`).then(
          (r) => r.text(),
        );
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`[data-section="${id}"]`);
        if (!fresh) return;
        if (wasOpen !== null) {
          fresh.setAttribute('data-open', wasOpen);
          fresh.setAttribute(
            'aria-hidden',
            wasOpen === 'true' ? 'false' : 'true',
          );
        }
        host.replaceWith(fresh);
      }),
    );
  }

  function toast(message) {
    let el = document.querySelector('[data-toast]');
    if (!el) {
      el = document.createElement('div');
      el.setAttribute('data-toast', '');
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.className =
        'fixed bottom-6 left-1/2 z-[200] -translate-x-1/2 rounded-full bg-rust px-5 py-3 font-mono text-[11px] tracking-[0.12em] uppercase text-bone shadow-lg transition-opacity duration-300';
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.style.opacity = '1';
    clearTimeout(el._t);
    el._t = setTimeout(() => {
      el.style.opacity = '0';
    }, 4000);
  }

  async function addToCart(id, quantity) {
    const res = await postJSON('/cart/add.js', {
      items: [{id, quantity: quantity || 1}],
    });
    // Shopify returns {status, message, description} on error (e.g. sold out)
    if (res && (res.status >= 400 || res.description)) {
      toast(res.description || res.message || 'Could not add to cart');
      return;
    }
    await rerenderCart();
    openDrawer('cart');
  }

  async function changeLine(key, quantity) {
    await postJSON('/cart/change.js', {id: key, quantity});
    await rerenderCart();
  }

  document.addEventListener('click', async (e) => {
    const add = e.target.closest('[data-add]');
    if (add) {
      e.preventDefault();
      const id = add.getAttribute('data-add');
      const qtyInput = document.querySelector('[data-qty-input]');
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      add.setAttribute('disabled', 'true');
      try {
        await addToCart(id, qty);
      } finally {
        add.removeAttribute('disabled');
      }
      return;
    }

    const step = e.target.closest('[data-qty]');
    if (step) {
      const line = step.closest('[data-line-key]');
      if (!line) return;
      const key = line.getAttribute('data-line-key');
      const current = parseInt(line.getAttribute('data-line-qty') || '1', 10);
      const next =
        step.getAttribute('data-qty') === 'inc' ? current + 1 : current - 1;
      await changeLine(key, Math.max(0, next));
      return;
    }

    const remove = e.target.closest('[data-remove]');
    if (remove) {
      e.preventDefault();
      const line = remove.closest('[data-line-key]');
      if (line) await changeLine(line.getAttribute('data-line-key'), 0);
    }
  });

  /* ---------- Quantity stepper (PDP, non-cart) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qstep]');
    if (!btn) return;
    const wrap = btn.closest('[data-qty-wrap]');
    const input = wrap && wrap.querySelector('[data-qty-input]');
    if (!input) return;
    const v = parseInt(input.value, 10) || 1;
    input.value = Math.max(
      1,
      btn.getAttribute('data-qstep') === 'inc' ? v + 1 : v - 1,
    );
  });

  /* ---------- Product gallery thumbnails ---------- */
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-gallery-thumb]');
    if (!thumb) return;
    const url = thumb.getAttribute('data-image');
    const main = document.querySelector('[data-gallery-main]');
    if (main && url) {
      main.setAttribute('src', url);
      main.setAttribute('srcset', '');
    }
    document
      .querySelectorAll('[data-gallery-thumb]')
      .forEach((t) =>
        t.setAttribute('data-active', t === thumb ? 'true' : 'false'),
      );
  });

  /* ---------- Predictive search ---------- */
  const searchResults = () =>
    document.querySelector('[data-predictive-results]');
  let searchTimer = null;

  document.addEventListener('input', (e) => {
    if (!e.target.matches('[data-predictive-input]')) return;
    const q = e.target.value.trim();
    clearTimeout(searchTimer);
    const out = searchResults();
    if (!out) return;
    if (q.length < 2) {
      out.innerHTML = '';
      return;
    }
    searchTimer = setTimeout(async () => {
      const url = `/search/suggest?q=${encodeURIComponent(
        q,
      )}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
      try {
        const html = await fetch(url).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('[data-predictive-results]');
        out.innerHTML = inner ? inner.innerHTML : '';
      } catch {
        out.innerHTML = '';
      }
    }, 220);
  });

  /* ---------- Collection: grid density + filters ---------- */
  document.addEventListener('click', (e) => {
    const dens = e.target.closest('[data-density]');
    if (dens) {
      const grid = document.querySelector('[data-product-grid]');
      if (grid) {
        const cols = dens.getAttribute('data-density');
        grid.setAttribute('data-cols', cols);
        document
          .querySelectorAll('[data-density]')
          .forEach((b) =>
            b.setAttribute('data-active', b === dens ? 'true' : 'false'),
          );
      }
    }
  });

  // Filter form: submit via Section Rendering API (no full reload)
  document.addEventListener('change', async (e) => {
    const form = e.target.closest('[data-filter-form]');
    if (!form) return;
    const sectionId = form.getAttribute('data-section-id');
    const params = new URLSearchParams(new FormData(form)).toString();
    const url = `${location.pathname}?${params}`;
    history.replaceState({}, '', url);
    const host = document.querySelector(`[data-section="${sectionId}"]`);
    if (!host) return;
    const html = await fetch(`${url}&section_id=${sectionId}`).then((r) =>
      r.text(),
    );
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector(`[data-section="${sectionId}"]`);
    if (fresh) host.replaceWith(fresh);
  });
})();
